import { Check, ChevronDown } from 'lucide-react'
import { useEffect, useRef, useState, type ReactNode } from 'react'

export interface Option<T> { value: T; label: string; hint?: string; icon?: ReactNode; group?: string }

/**
 * Menu déroulant léger pour les filtres. `null` = « Tous ».
 * `presets` : choix principaux en tête du menu, avant la liste détaillée ; `presetsBefore`
 * d'entre eux passent avant « Tous ».
 */
export function Select<T extends string>({ label, value, options, presets = [], presetsBefore = 0, allLabel, onChange, icon, quiet = false, wide = false }: {
  label: string
  value: T | null
  options: Option<T>[]
  presets?: Option<T>[]
  presetsBefore?: number
  /** Valeur par défaut : affichée, mais sans le style « filtre actif ». */
  quiet?: boolean
  allLabel: string
  onChange: (v: T | null) => void
  icon?: ReactNode
  /** Menu plus large et plus haut, pour les longues listes (comptes). */
  wide?: boolean
}) {
  const [open, setOpen] = useState(false)
  const ref = useRef<HTMLDivElement>(null)

  useEffect(() => {
    if (!open) return
    const onDown = (e: MouseEvent) => { if (!ref.current?.contains(e.target as Node)) setOpen(false) }
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') setOpen(false) }
    document.addEventListener('mousedown', onDown)
    document.addEventListener('keydown', onKey)
    return () => { document.removeEventListener('mousedown', onDown); document.removeEventListener('keydown', onKey) }
  }, [open])

  const current = [...presets, ...options].find(o => o.value === value)
  const showValue = value !== null
  const active = showValue && !quiet
  let lastGroup: string | undefined

  return (
    <div className="relative" ref={ref}>
      <button
        onClick={() => setOpen(o => !o)}
        aria-haspopup="listbox"
        aria-expanded={open}
        className={`h-9 inline-flex items-center gap-2 rounded-xl border px-3 text-sm transition-colors whitespace-nowrap ${
          active ? 'border-primary/40 bg-primary/10 text-foreground' : 'border-border bg-card text-muted-foreground hover:text-foreground hover:bg-muted'
        }`}
      >
        {icon}
        <span className={active ? 'text-muted-foreground' : ''}>{label}</span>
        {showValue && <span className={`max-w-[140px] truncate ${active ? 'font-medium text-foreground' : ''}`}>{current?.label}</span>}
        <ChevronDown size={14} className={`transition-transform ${open ? 'rotate-180' : ''}`} />
      </button>
      {open && (
        <div role="listbox" className={`anim-pop absolute z-40 mt-1.5 ${wide ? 'min-w-[min(320px,calc(100vw-2rem))] max-h-[min(560px,70vh)]' : 'min-w-[220px] max-h-[340px]'} overflow-auto rounded-xl border border-border bg-card p-1 shadow-xl shadow-black/20`}>
          {presets.slice(0, presetsBefore).map(o => (
            <Item key={o.value} selected={value === o.value} onClick={() => { onChange(o.value); setOpen(false) }} hint={o.hint} icon={o.icon}>{o.label}</Item>
          ))}
          <Item selected={value === null} onClick={() => { onChange(null); setOpen(false) }}>{allLabel}</Item>
          {presets.slice(presetsBefore).map(o => (
            <Item key={o.value} selected={value === o.value} onClick={() => { onChange(o.value); setOpen(false) }} hint={o.hint} icon={o.icon}>{o.label}</Item>
          ))}
          <div className="h-px bg-border my-1" />
          {options.map(o => {
            const header = o.group && o.group !== lastGroup ? o.group : null
            lastGroup = o.group
            return (
              <div key={o.value}>
                {header && <div className="px-2.5 pt-2 pb-1 text-[11px] font-medium text-muted-foreground/70">{header}</div>}
                <Item selected={value === o.value} onClick={() => { onChange(o.value); setOpen(false) }} hint={o.hint} icon={o.icon}>
                  {o.label}
                </Item>
              </div>
            )
          })}
        </div>
      )}
    </div>
  )
}

function Item({ selected, onClick, children, hint, icon }: { selected: boolean; onClick: () => void; children: ReactNode; hint?: string; icon?: ReactNode }) {
  return (
    <button
      role="option"
      aria-selected={selected}
      onClick={onClick}
      className={`w-full flex items-center gap-2.5 rounded-lg px-2.5 py-2 text-sm text-left transition-colors ${selected ? 'bg-muted text-foreground' : 'text-muted-foreground hover:bg-muted hover:text-foreground'}`}
    >
      {icon}
      <span className="flex-1 min-w-0">
        <span className="block truncate">{children}</span>
        {hint && <span className="block text-xs text-muted-foreground/70 truncate">{hint}</span>}
      </span>
      {selected && <Check size={14} className="text-primary shrink-0" />}
    </button>
  )
}
